/**
 * Quem ocupa a produção: a fatia de cada marca na carga programada de um período.
 *
 * NENHUMA conta nova. As horas são os minutos que o MOTOR programou para cada OP em cada
 * semana (`minutosProgramados` do quadrinho, que soma exatamente o `horasProgramadas`
 * dele), e as peças e o faturamento são os da própria OP na Carteira. O que esta tela faz
 * é agrupar por marca e, quando a OP atravessa a borda do período, pegar a PARTE dela que
 * cai dentro, na proporção dos minutos que o motor pôs ali.
 *
 * Por isso, num período que cobre a grade inteira, a proporção de toda OP é 1 e as somas
 * fecham com os cartões da Carteira. É o que o spec ao lado prova.
 */
import { GradeQuadro, GradeSemana } from '@/domain/mapa/interfaces/mapa';
import { EnrichedOp } from '@/domain/ops/interfaces/ops';

/** As três medidas que a chave do bloco alterna. */
export type TMedidaDaProducao = 'HORAS' | 'PECAS' | 'FATURAMENTO';

/** Quantas marcas a pizza desenha antes de juntar o resto em "Outras". */
export const MARCAS_NA_PIZZA = 5;

const MINUTOS_POR_HORA = 60;

/** Uma fatia da pizza: uma marca, ou o agrupado "Outras". */
export interface IFatiaDaProducao {
  /** O nome da marca; vazio na OP sem marca preenchida. */
  marca: string;
  horas: number;
  pecas: number;
  faturamento: number;
  /** Quantas OPs ativas da marca têm carga no período. */
  ops: number;
}

export interface IOcupacaoPorMarca {
  /** As marcas com carga no período, da maior para a menor na medida escolhida. */
  fatias: Array<IFatiaDaProducao>;
  /** O total do período na medida escolhida: o denominador das porcentagens. */
  total: number;
  /** Todas as OPs ativas com carga no período. */
  ops: number;
}

/** O valor de uma fatia na medida escolhida. */
export function valorDaMedida(fatia: IFatiaDaProducao, medida: TMedidaDaProducao): number {
  if (medida === 'HORAS') {
    return fatia.horas;
  }
  return medida === 'PECAS' ? fatia.pecas : fatia.faturamento;
}

interface IMinutosDaOp {
  /** Minutos que o motor pôs nesta OP DENTRO do período. */
  noPeriodo: number;
  /** Minutos dela na grade inteira: o denominador da proporção. */
  total: number;
  marca: string;
}

/**
 * Os minutos de cada OP, dentro do período e no total da grade. A grade já traz a carga
 * repartida por OP, então aqui é só somar o que o motor decidiu.
 */
function minutosPorOp(
  quadros: Array<GradeQuadro>,
  semanasDoPeriodo: ReadonlySet<string>,
): Map<string, IMinutosDaOp> {
  const porOp = new Map<string, IMinutosDaOp>();

  for (const quadro of quadros) {
    const dentro = semanasDoPeriodo.has(quadro.semanaInicio);
    for (const op of quadro.ops ?? []) {
      const atual = porOp.get(op.uid) ?? { noPeriodo: 0, total: 0, marca: (op.marca ?? '').trim() };
      atual.total += op.minutosProgramados;
      if (dentro) {
        atual.noPeriodo += op.minutosProgramados;
      }
      porOp.set(op.uid, atual);
    }
  }
  return porOp;
}

interface IOcupacaoPorMarcaParams {
  /** A grade inteira: o período é um recorte dela, nunca uma leitura à parte. */
  quadros: Array<GradeQuadro>;
  semanas: Array<GradeSemana>;
  /** A carteira, para as peças e o faturamento de cada OP, pela mesma fonte da Carteira. */
  ops: Array<EnrichedOp>;
  medida: TMedidaDaProducao;
}

/**
 * A ocupação da produção por marca no período, já ordenada pela medida escolhida.
 *
 * A OP que atravessa a borda entra pela PARTE dela que cai dentro: a proporção são os
 * minutos no período sobre os minutos dela na grade. Em horas isso é exato por
 * construção; em peças e faturamento é a melhor repartição possível, porque o motor
 * programa minutos e não peças avulsas.
 */
export function ocupacaoPorMarca({
  quadros,
  semanas,
  ops,
  medida,
}: IOcupacaoPorMarcaParams): IOcupacaoPorMarca {
  const noPeriodo = new Set(semanas.map((semana) => semana.inicio));
  const porOp = minutosPorOp(quadros, noPeriodo);
  const carteiraPorUid = new Map(ops.map((op) => [op.uid, op]));

  const porMarca = new Map<string, IFatiaDaProducao>();
  for (const [uid, minutos] of porOp) {
    if (minutos.noPeriodo <= 0 || minutos.total <= 0) {
      continue;
    }
    const fracao = minutos.noPeriodo / minutos.total;
    const daCarteira = carteiraPorUid.get(uid);
    const atual = porMarca.get(minutos.marca) ?? {
      marca: minutos.marca,
      horas: 0,
      pecas: 0,
      faturamento: 0,
      ops: 0,
    };

    atual.horas += minutos.noPeriodo / MINUTOS_POR_HORA;
    atual.pecas += (daCarteira?.saldo ?? 0) * fracao;
    atual.faturamento += (daCarteira?.faturamento ?? 0) * fracao;
    atual.ops += 1;
    porMarca.set(minutos.marca, atual);
  }

  const fatias = [...porMarca.values()].sort(
    (a, b) => valorDaMedida(b, medida) - valorDaMedida(a, medida) || a.marca.localeCompare(b.marca, 'pt-BR'),
  );

  return {
    fatias,
    total: fatias.reduce((soma, fatia) => soma + valorDaMedida(fatia, medida), 0),
    ops: fatias.reduce((soma, fatia) => soma + fatia.ops, 0),
  };
}

/** A pizza desenhada: as maiores com cor própria e o resto junto em "Outras". */
export interface IPizzaDaProducao {
  maiores: Array<IFatiaDaProducao>;
  /** `null` quando não sobrou ninguém depois das maiores. */
  outras: IFatiaDaProducao | null;
  /** As marcas que entraram em "Outras": é por elas que o clique filtra a Carteira. */
  marcasDeOutras: Array<string>;
}

/** Junta tudo a partir da 6ª marca numa fatia só, somando as medidas e as OPs. */
export function pizzaDaProducao(fatias: Array<IFatiaDaProducao>): IPizzaDaProducao {
  const maiores = fatias.slice(0, MARCAS_NA_PIZZA);
  const resto = fatias.slice(MARCAS_NA_PIZZA);

  if (resto.length === 0) {
    return { maiores, outras: null, marcasDeOutras: [] };
  }

  const outras = resto.reduce<IFatiaDaProducao>(
    (soma, fatia) => ({
      marca: '',
      horas: soma.horas + fatia.horas,
      pecas: soma.pecas + fatia.pecas,
      faturamento: soma.faturamento + fatia.faturamento,
      ops: soma.ops + fatia.ops,
    }),
    { marca: '', horas: 0, pecas: 0, faturamento: 0, ops: 0 },
  );

  return { maiores, outras, marcasDeOutras: resto.map((fatia) => fatia.marca) };
}
